import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Image as ImageIcon, ToggleLeft, ToggleRight, Info } from 'lucide-react';
import AddEditBannerModal from '../components/modals/AddEditBannerModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ApiService from '../services/api';
import toast from 'react-hot-toast';

// Keep in sync with AddEditBannerModal
const RESOLUTION_HINT = '1080 × 400 px (landscape, 16:6 ratio) — JPEG or PNG, max 2 MB';

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllBanners();
      if (response.success) {
        setBanners(response.data.banners || []);
      }
    } catch (error) {
      toast.error('Failed to load banners');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedBanner(null);
    setIsModalOpen(true);
  };

  const handleEdit = (banner) => {
    setSelectedBanner(banner);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (banner) => {
    setSelectedBanner(banner);
    setIsDeleteModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      setModalLoading(true);
      if (selectedBanner) {
        await ApiService.updateBanner(selectedBanner.id, formData);
        toast.success('Banner updated successfully');
      } else {
        await ApiService.createBanner(formData);
        toast.success('Banner created successfully');
      }
      setIsModalOpen(false);
      fetchBanners();
    } catch (error) {
      toast.error(error.message || 'Failed to save banner');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBanner) return;
    try {
      await ApiService.deleteBanner(selectedBanner.id);
      toast.success('Banner deleted successfully');
      setIsDeleteModalOpen(false);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await ApiService.updateBanner(banner.id, { isActive: !banner.isActive });
      toast.success(`Banner ${!banner.isActive ? 'activated' : 'deactivated'}`);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to update banner status');
    }
  };

  const filtered = banners.filter((b) => {
    const q = searchTerm.toLowerCase();
    return (
      b.title?.toLowerCase().includes(q) ||
      b.subtitle?.toLowerCase().includes(q) ||
      b.imageUrl?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banners</h1>
          <p className="text-sm text-gray-500 mt-1">Manage promotional banners displayed on the app home screen</p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Banner
        </button>
      </div>

      {/* Resolution hint */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-700">
          <span className="font-medium">Recommended resolution:</span> {RESOLUTION_HINT}
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search banners..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-full text-sm"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
              <div className="w-full h-36 bg-gray-200" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {searchTerm ? 'No banners match your search' : 'No banners yet. Click "Add Banner" to create one.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((banner) => (
            <div
              key={banner.id}
              className={`bg-white rounded-xl border overflow-hidden transition-shadow hover:shadow-md ${
                banner.isActive ? 'border-gray-200' : 'border-gray-200 opacity-60'
              }`}
            >
              {/* Image Preview */}
              <div className="relative w-full h-36 bg-gray-100">
                {banner.imageUrl ? (
                  <img
                    src={banner.imageUrl}
                    alt={banner.title || 'Banner'}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-10 h-10 text-gray-300" />
                  </div>
                )}
                {/* Priority badge */}
                <span className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded-full">
                  #{banner.priority}
                </span>
                {/* Status badge */}
                <span
                  className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-medium ${
                    banner.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {banner.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="p-4">
                {banner.title && (
                  <p className="font-medium text-gray-900 text-sm truncate">{banner.title}</p>
                )}
                {banner.subtitle && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">{banner.subtitle}</p>
                )}
                {banner.linkType !== 'none' && (
                  <p className="text-xs text-blue-600 truncate mt-1">
                    {banner.linkType === 'url' ? '🔗 ' : '📱 '}{banner.linkValue}
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleToggleActive(banner)}
                    title={banner.isActive ? 'Deactivate' : 'Activate'}
                    className="text-gray-500 hover:text-orange-600 transition-colors"
                  >
                    {banner.isActive ? (
                      <ToggleRight className="w-5 h-5 text-green-600" />
                    ) : (
                      <ToggleLeft className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(banner)}
                    className="inline-flex items-center px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                  >
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteClick(banner)}
                    className="inline-flex items-center px-2 py-1 text-xs text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors ml-auto"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AddEditBannerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        banner={selectedBanner}
        loading={modalLoading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Banner"
        message={`Are you sure you want to delete this banner${selectedBanner?.title ? ` "${selectedBanner.title}"` : ''}? This action cannot be undone.`}
      />
    </div>
  );
};

export default Banners;
